/**
 * The archetype populations the Trust Center can be filtered to.
 *
 * One list, read by the page that renders the chips and by every tab that needs to name or
 * re-apply the operator's choice. The keys match the backend's `AgentArchetypeScope`, so the
 * population the page shows and the population the board report is generated over are selected by
 * the same word travelling end to end rather than by two tables that have to be kept in step.
 */

export const TRUST_SCOPES = Object.freeze([
  { key: 'all', label: 'All agents', archetype: null,
    description: 'Lets + AutoLets + ExteLets (+ SysLets for Platform Admin)' },
  { key: 'let', label: 'Lets', archetype: 'Let',
    description: 'Agentlets built and protected here' },
  /* Lets and AutoLets were one chip until now, which hid the distinction an operator acts on: a
   * runbook-managed agent changes when its runbook does, and is governed accordingly. */
  { key: 'autolet', label: 'AutoLets', archetype: 'AutoLet',
    description: 'Agentlets generated and kept in step with a runbook' },
  { key: 'extelet', label: 'ExteLets', archetype: 'ExteLet',
    description: 'External agents (vendor / bring-your-own)' },
  /* SysLets are platform-owned system agents — Platform Admin only. A separate scope so platform
   * agents never mix into an operator's own fleet. */
  { key: 'syslet', label: 'SysLets', archetype: 'SysLet',
    description: 'Platform system agents (Platform Admin only)', paOnly: true },
]);

const byKey = (key) => TRUST_SCOPES.find((s) => s.key === key) || null;

/** The archetype a scope selects, or null for the whole visible fleet. */
export function scopeArchetype(key) {
  const s = byKey(key);
  return s ? s.archetype : null;
}

/** The operator's own wording for a scope, for a sentence that has to name it. */
export function scopeLabel(key) {
  const s = byKey(key);
  return s ? s.label : 'All agents';
}

/** Lower-cased for mid-sentence use: "No anomalies in ExteLets." vs "across all agents". */
export function scopeNoun(key) {
  return key === 'all' ? 'the fleet' : scopeLabel(key);
}
